import React, { useEffect, useState } from "react";
import axios from "axios";
import BookAppointment from "../PatientLogin/BookAppointment";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Navbar,
  Nav,
  Dropdown,
  Container,
  Spinner,
} from "react-bootstrap";

const PopupAlert = ({ message, onClose }) => {
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [message, onClose]);

  if (!message) return null;

  return (
    <>
      <div className="popup-backdrop" />
      <div className="popup-alert">
        <div>{message}</div>
      </div>
      <style jsx="true">{`
        .popup-backdrop {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.3);
          z-index: 1050;
        }
        .popup-alert {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: #4fc3f7;
          color: white;
          padding: 1.25rem 2rem;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1.2rem;
          box-shadow: 0 6px 18px rgba(79, 195, 247, 0.7);
          z-index: 1051;
          user-select: none;
          text-align: center;
          min-width: 240px;
        }
      `}</style>
    </>
  );
};

const PatientDashboard = ({ userId: propUserId }) => {
  // Use local state userId that persists via localStorage
  const [userId, setUserId] = useState(() => {
    // If propUserId exists (on first login), store it
    if (propUserId) {
      localStorage.setItem("patientUserId", propUserId);
      return propUserId;
    }
    // Otherwise try to get from localStorage (after reload)
    return localStorage.getItem("patientUserId") || null;
  });

  const [queries, setQueries] = useState([]);
  const [patientInfo, setPatientInfo] = useState({ name: "", email: "" });
  const [showModal, setShowModal] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState({});
  const [services, setServices] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) {
      // If no userId at all, redirect to login page
      window.location.href = "/patient/login";
      return;
    }
    const fetchData = async () => {
      setLoading(true);
      try {
        const [patientRes, queriesRes, servicesRes] = await Promise.all([
          axios.get(`https://ricgop.online/api/get-patient-info.php?userId=${userId}`),
          axios.get(`https://ricgop.online/api/get-queries.php?userId=${userId}`),
          axios.get("https://ricgop.online/api/get_services.php"),
        ]);
        setPatientInfo(patientRes.data);
        setQueries(queriesRes.data);
        setServices(servicesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        setPopupMessage("Failed to fetch data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  const handleEventClick = (info) => {
    const query = queries.find((q) => q.id.toString() === info.event.id);
    setSelectedQuery({
      ...query,
      date: query.date || "",
      time: query.time || "",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      await axios.post("https://ricgop.online/api/update-query.php", {
        id: selectedQuery.id,
        explanation: selectedQuery.explanation,
        service: selectedQuery.service,
        document: selectedQuery.document,
        date: selectedQuery.date,
        time: selectedQuery.time,
      });
      setShowModal(false);
      const res = await axios.get(
        `https://ricgop.online/api/get-queries.php?userId=${userId}`
      );
      setQueries(res.data);
      setPopupMessage("Appointment updated successfully!");
    } catch (error) {
      console.error("Error saving query:", error);
      setPopupMessage("Failed to save appointment. Try again.");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.post("https://ricgop.online/api/delete-query.php", {
        id: selectedQuery.id,
      });
      setShowModal(false);
      const res = await axios.get(
        `https://ricgop.online/api/get-queries.php?userId=${userId}`
      );
      setQueries(res.data);
      setPopupMessage("Appointment deleted successfully!");
    } catch (error) {
      console.error("Error deleting query:", error);
      setPopupMessage("Failed to delete appointment. Try again.");
    }
  };

  const events = queries.map((q) => ({
    id: q.id.toString(),
    title: q.doctor || "Appointment",
    start: `${q.date}T${q.time}`,
  }));

  const handleLogout = () => {
    localStorage.removeItem("patientUserId");
    window.location.href = "/patient/login";
  };

  return (
    <div className="dashboard-wrapper d-flex">
  <aside className="sidebar">
    <div className="sidebar-logo text-center mt-3">
      <img src="../../assets/img/logo/transparent-logo.png" alt="Logo" style={{ width: '70px', height: 'auto' }} />
    </div>
    <div className="sidebar-header mt-2">Patient Panel</div>
    <nav className="nav flex-column mt-4">
      <a href="#" className="nav-link active">Dashboard</a>
    </nav>
  </aside>

      <div className="main-content flex-grow-1 d-flex flex-column">
        <Navbar className="navbar-custom px-4" expand="lg">
          <Navbar.Brand className="text-white fw-bold">Patient Portal</Navbar.Brand>
          <Nav className="ms-auto d-flex align-items-center">
            <Button
              variant="light"
              className="me-3 fw-semibold shadow-sm"
              onClick={() => setShowBookingModal(true)}
            >
              Book New Appointment
            </Button>
            <Dropdown align="end">
              <Dropdown.Toggle
                variant="light"
                className="fw-semibold shadow-sm"
                style={{ minWidth: "130px" }}
              >
                {patientInfo.name || "Patient"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleLogout}>
                  Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Nav>
        </Navbar>

        <Container fluid className="py-4">
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-primary fw-semibold">Loading data...</p>
            </div>
          ) : (
            <>
              <div className="welcome-box text-center mb-5">
                <h4 className="mb-1 text-primary fw-bold">
                  Welcome, {patientInfo.name || "Patient"}
                </h4>
                <p className="text-muted fs-6">{patientInfo.email}</p>
              </div>

              <div className="calendar-section p-4 rounded bg-white shadow-sm">
                <h5 className="mb-4 text-center text-primary fw-semibold">
                  Your Appointment Queries
                </h5>
                <FullCalendar
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  events={events}
                  eventClick={handleEventClick}
                  height="auto"
                  dayMaxEvents={true}
                  headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,dayGridWeek",
                  }}
                  eventColor="#4fc3f7"
                  eventTextColor="#fff"
                  aspectRatio={1.5}
                />
              </div>
            </>
          )}
        </Container>

        <footer className="footer text-center py-3 border-top">
          <small className="text-muted">&copy; 2025 Patient Portal</small>
        </footer>

        <BookAppointment
          show={showBookingModal}
          onHide={() => setShowBookingModal(false)}
          userId={userId}
        />

        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          centered
          size="lg"
          dialogClassName="edit-query-modal"
        >
          <Modal.Header closeButton className="text-dark">
            <Modal.Title>Edit Appointment Query</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="gy-3">
                <Col md={6}>
                  <Form.Group controlId="serviceSelect">
                    <Form.Label>Service</Form.Label>
                    <Form.Select
                      value={selectedQuery.service || ""}
                      onChange={(e) =>
                        setSelectedQuery({
                          ...selectedQuery,
                          service: e.target.value,
                        })
                      }
                    >
                      <option value="">Select a Service</option>
                      {services.map((service) => (
                        <option key={service.id} value={service.service}>
                          {service.service}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="dateInput">
                    <Form.Label>Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={selectedQuery.date || ""}
                      onChange={(e) =>
                        setSelectedQuery({
                          ...selectedQuery,
                          date: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="timeInput">
                    <Form.Label>Time</Form.Label>
                    <Form.Control
                      type="time"
                      value={selectedQuery.time || ""}
                      onChange={(e) =>
                        setSelectedQuery({
                          ...selectedQuery,
                          time: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="explanationInput">
                    <Form.Label>Explanation</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={selectedQuery.explanation || ""}
                      onChange={(e) =>
                        setSelectedQuery({
                          ...selectedQuery,
                          explanation: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="documentInput">
                    <Form.Label>Document Upload URL</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Provide document URL"
                      value={selectedQuery.document || ""}
                      onChange={(e) =>
                        setSelectedQuery({
                          ...selectedQuery,
                          document: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="danger"
              onClick={handleDelete}
              className="me-auto"
            >
              Delete
            </Button>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>

        <PopupAlert
          message={popupMessage}
          onClose={() => setPopupMessage("")}
        />
      </div>

      <style jsx>{`

      .sidebar {
  width: 250px;
  background: linear-gradient(to bottom, #00b4db, #0083b0); /* Background gradient */
  color: white;
  padding: 20px;
  min-height: 100vh;
}

.sidebar-header {
  font-size: 1.5rem;
  font-weight: bold;
  text-align: center;
}

        .dashboard-wrapper {
          min-height: 100vh;
          background: #eef6fb;
        }
        .sidebar {
          width: 220px;
          background: linear-gradient(to right, #00b4db, #0083b0); /* Blue gradient */
          color: #fff;
          padding: 1.5rem 1rem;
          display: flex;
          flex-direction: column;
          font-weight: 600;
          font-size: 1.1rem;
        }
        .sidebar-header {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          text-align: center;
        }
        .sidebar .nav-link {
          color: #fff;
          margin-bottom: 0.6rem;
          padding: 0.4rem 0.5rem;
          border-radius: 4px;
        }
        .sidebar .nav-link.active,
        .sidebar .nav-link:hover {
          background: #3f85f8;
          color: white;
        }
        .navbar-custom {
          background: linear-gradient(to right, #00b4db, #0083b0); /* Blue gradient */
        }
        .welcome-box {
          background: white;
          border-radius: 12px;
          padding: 1rem 2rem;
          box-shadow: 0 0 12px rgba(0, 0, 0, 0.08);
          font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
        }
        .calendar-section {
          font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
        }
        footer.footer {
          background: white;
        }
        .edit-query-modal .modal-content {
          border-radius: 16px;
        }
      `}</style>
    </div>
  );
};

export default PatientDashboard;
